import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Search, Copy } from "lucide-react";
import { toast } from "sonner";
import DocumentVault from "../../components/DocumentVault";

const CM_DOC_TYPES = [
  { value: "agency_record", label: "Agency Record" },
  { value: "referral_letter", label: "Referral Letter" },
  { value: "housing_doc", label: "Housing Document" },
  { value: "income_proof", label: "Proof of Income" },
  { value: "gov_id", label: "Client Government ID" },
  { value: "veteran_doc", label: "Veteran Documentation" },
  { value: "other", label: "Other" },
];

export default function CaseManagerDashboard() {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [agency, setAgency] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/referrals/sent");
      setReferrals(r.data);
    } catch { setReferrals([]); }
    if (user.role === "agency_owner" || user.role === "agency_staff") {
      try {
        const ar = await api.get("/agencies/me");
        setAgency(ar.data);
      } catch (e) {
        console.warn("[CaseManagerDashboard] /agencies/me failed:", e?.response?.status || e?.message);
      }
    }
  }, [user.role]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    total: referrals.length,
    placed: referrals.filter((r) => ["placed", "completed"].includes(r.status)).length,
    pending: referrals.filter((r) => ["submitted", "under_review"].includes(r.status)).length,
    accepted: referrals.filter((r) => r.status === "accepted").length,
  }), [referrals]);

  const roleLabel = user.role === "agency_owner" ? "Agency Owner" : user.role === "agency_staff" ? "Agency Staff" : "Case Manager";

  const copyCode = () => {
    if (agency?.code) {
      navigator.clipboard.writeText(agency.code);
      toast.success("Agency code copied — share with your team");
    }
  };

  return (
    <div data-testid="case-manager-dashboard" className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-2">{roleLabel}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">{user.organization || user.full_name}</h1>
          {user.subscription_tier && <Badge className="mt-2 bg-[#C6F6D5] text-[#22543D]">Plan: {user.subscription_tier}</Badge>}
        </div>
        <Link to="/search"><Button data-testid="cm-search" className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white"><Search className="w-4 h-4 mr-1.5" /> Find Housing for Client</Button></Link>
      </div>

      {agency && (
        <div className="mb-8 bg-white border border-[#D1D5DB] p-5 rounded-sm">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4A5568]">Agency</div>
              <div className="font-heading font-semibold text-[#0A192F] text-lg">{agency.name}</div>
              <div className="text-sm text-[#4A5568] mt-1">Members: {agency.members?.length || 0}</div>
            </div>
            {user.role === "agency_owner" && (
              <div className="flex items-center gap-2">
                <div className="text-xs text-[#4A5568]">Invite code:</div>
                <code data-testid="agency-code" className="px-3 py-1.5 bg-[#F5F5F0] border border-[#D1D5DB] rounded-sm font-mono text-sm">{agency.code}</code>
                <button onClick={copyCode} data-testid="copy-agency-code" className="p-2 hover:bg-[#F5F5F0] rounded"><Copy className="w-4 h-4 text-[#4A5568]" /></button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Stat title="Total Referrals" value={stats.total} />
        <Stat title="Pending" value={stats.pending} />
        <Stat title="Accepted" value={stats.accepted} />
        <Stat title="Placements" value={stats.placed} accent />
      </div>

      <Tabs defaultValue="referrals">
        <TabsList>
          <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="referrals">
          <div className="bg-white border border-[#D1D5DB] rounded-sm">
            <div className="px-5 py-3 border-b border-[#D1D5DB] font-heading font-semibold text-[#0A192F]">Referral History</div>
            {referrals.length === 0 ? (
              <div className="p-10 text-center text-sm text-[#4A5568]">No referrals yet — browse housing and refer a client.</div>
            ) : (
              <div className="divide-y divide-[#D1D5DB]">
                {referrals.map((r) => (
                  <div key={r.id} data-testid={`cm-referral-${r.id}`} className="px-5 py-4 flex flex-wrap items-center gap-3 justify-between">
                    <div>
                      <div className="font-medium text-[#0A192F]">{r.client_name} {r.veteran_status && <Badge variant="outline" className="ml-1">Veteran</Badge>}</div>
                      <div className="text-xs text-[#4A5568]">→ {r.listing_name}</div>
                    </div>
                    <div className="flex gap-2 items-center">
                      <span className="text-xs text-[#4A5568]">{new Date(r.created_at).toLocaleDateString()}</span>
                      <Badge className={statusColor(r.status)}>{r.status.replace('_', ' ')}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <DocumentVault
            title={user.role === "agency_owner" ? "Agency Documents" : "Case Manager Documents"}
            description="Upload referral letters, agency records, client documentation, and supporting files. Documents can be linked to specific referrals."
            docTypes={CM_DOC_TYPES}
            testId="cm-document-vault"
          />
        </TabsContent>
      </Tabs>

      {!user.subscription_tier && (
        <div className="mt-10 bg-[#0A192F] text-white p-8 rounded-sm">
          <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-2">Upgrade</div>
          <h2 className="font-heading text-2xl font-bold">Unlock the Professional dashboard</h2>
          <p className="mt-2 text-white/70 max-w-xl">Vacancy alerts, analytics, and priority support for $19.99/mo. Agency accounts roll up unlimited team members.</p>
          <Link to="/pricing"><Button data-testid="cm-upgrade" className="mt-5 bg-[#C05621] hover:bg-[#9C4221] text-white">See Plans</Button></Link>
        </div>
      )}
    </div>
  );
}

const Stat = ({ title, value, accent }) => (
  <div className="bg-white border border-[#D1D5DB] p-4 rounded-sm">
    <div className="text-xs uppercase tracking-widest text-[#4A5568]">{title}</div>
    <div className={`font-heading text-3xl font-bold mt-1 ${accent ? "text-[#C05621]" : "text-[#0A192F]"}`}>{value}</div>
  </div>
);

const statusColor = (s) => {
  const m = {
    submitted: "bg-[#FEEBC8] text-[#7B341E]",
    under_review: "bg-[#BEE3F8] text-[#2A4365]",
    accepted: "bg-[#E9D8FD] text-[#44337A]",
    waitlisted: "bg-[#FAF089] text-[#744210]",
    placed: "bg-[#C6F6D5] text-[#22543D]",
    completed: "bg-[#276749] text-white",
    closed: "bg-[#1A202C] text-white",
    rejected: "bg-[#FED7D7] text-[#742A2A]",
  };
  return m[s] || "bg-[#F5F5F0] text-[#0A192F]";
};
